import { BookCallButton } from "@/components/shared/BookCallButton";
import { ContactTimePicker, formatContactTime } from "@/components/shared/ContactTimePicker";
import { useState, useEffect, useMemo } from "react";
import { PublicLayout } from "@/components/shared/PublicLayout";
import { SEOHead } from "@/components/shared/SEOHead";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { createLead, getPageContent } from "@/lib/api";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock, Loader2, Send } from "lucide-react";

const DEFAULT_FIELDS = [
  { name: "name", label: "Full Name", type: "text", required: true },
  { name: "email", label: "Email", type: "email", required: true },
  { name: "phone", label: "Phone", type: "tel", required: true },
  { name: "company", label: "Company Name", type: "text", required: false },
  { name: "message", label: "Message", type: "textarea", required: true, placeholder: "How can we help you?" },
];

function getSection(sections, name, fallback = {}) {
  const found = sections.find((s) => s.section_name === name && s.active !== false);
  if (!found) return fallback;
  return { ...fallback, ...found, metadata: { ...(fallback.metadata || {}), ...(found.metadata || {}) } };
}

export default function ContactPage() {
  const [loading, setLoading] = useState(false);
  const [sections, setSections] = useState([]);
  const [formData, setFormData] = useState({});

  useEffect(() => { getPageContent("contact").then(setSections).catch(() => {}); }, []);

  const hero = getSection(sections, "hero", { title: "Get in Touch", content: "Have questions about becoming a dealer or need support?", metadata: { subtitle: "Contact Us" } });
  const contactInfo = getSection(sections, "contact_info", { title: "Contact Information", metadata: { items: [] } });
  const formSection = getSection(sections, "form", { title: "Send Us a Message", content: "Fill out the form below and we'll get back to you within 24 hours." });

  const contactItems = contactInfo.metadata?.items || [];
  const formFields = useMemo(() => {
    const cmsFields = formSection.metadata?.fields;
    return (Array.isArray(cmsFields) && cmsFields.length > 0) ? cmsFields : DEFAULT_FIELDS;
  }, [formSection]);

  useEffect(() => {
    const initial = {};
    formFields.forEach((f) => { initial[f.name] = ""; });
    setFormData(initial);
  }, [formFields]);

  const handleChange = (e) => { setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value })); };
  const [contactTime, setContactTime] = useState({ date: "", time: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const when = formatContactTime(contactTime);
      await createLead({ ...formData, message: `${formData.message || ""}${when ? `\n\nBest time to reach: ${when}` : ""}`, source: "contact_form" });
      toast.success("Message sent successfully! We'll get back to you soon.");
      const reset = {};
      formFields.forEach((f) => { reset[f.name] = ""; });
      setFormData(reset);
      setContactTime({ date: "", time: "" });
    } catch (error) { toast.error("Failed to send message. Please try again."); }
    finally { setLoading(false); }
  };

  const iconForLabel = (label) => {
    const l = label?.toLowerCase() || "";
    if (l.includes("phone")) return Phone;
    if (l.includes("email") || l.includes("mail")) return Mail;
    if (l.includes("address") || l.includes("location")) return MapPin;
    if (l.includes("hour") || l.includes("time")) return Clock;
    return Phone;
  };

  const textFields = formFields.filter((f) => f.type !== "textarea");
  const textareaFields = formFields.filter((f) => f.type === "textarea");

  return (
    <PublicLayout>
      <SEOHead
        pageSlug="contact"
        fallback={{
          meta_title: "Contact Prime Media Wireless | Total Wireless Master Agent",
          meta_description: "Get in touch with Prime Media Wireless. Contact us for dealer inquiries, support, or to learn about our wireless dealer program.",
          canonical_url: `${window.location.origin}/contact`,
        }}
      />
      <section className="bg-navy py-16" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="label-style text-teal mb-4">{hero.metadata?.subtitle}</p>
            <h1 className="font-heading font-bold text-4xl lg:text-5xl text-white mb-6">{hero.title}</h1>
            <p className="text-slate-300 text-lg font-body">{hero.content}</p>
            <BookCallButton className="mt-8" />
          </div>
        </div>
      </section>

      <section className="py-16 bg-slate-50" data-testid="contact-content">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12">
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="lg:col-span-4">
              <h2 className="font-heading font-bold text-2xl text-navy mb-6">{contactInfo.title}</h2>
              <div className="space-y-6">
                {contactItems.map((item) => {
                  const Icon = iconForLabel(item.label);
                  return (
                    <div key={item.label} className="flex gap-4">
                      <div className="w-12 h-12 bg-teal/10 rounded-lg flex items-center justify-center flex-shrink-0"><Icon className="w-5 h-5 text-teal" /></div>
                      <div>
                        <p className="font-heading font-semibold text-navy">{item.label}</p>
                        {item.href ? (
                          <a href={item.href} className="text-slate-600 hover:text-teal transition-colors font-body">{item.value}</a>
                        ) : (
                          <p className="text-slate-600 whitespace-pre-line font-body">{item.value}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="lg:col-span-8">
              <div className="bg-white rounded-xl border border-slate-200 p-6 lg:p-8">
                <h2 className="font-heading font-bold text-2xl text-navy mb-2">{formSection.title}</h2>
                <p className="text-slate-600 mb-6 font-body">{formSection.content}</p>
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    {textFields.map((field) => (
                      <div key={field.name}>
                        <Label htmlFor={field.name}>{field.label}{field.required ? " *" : ""}</Label>
                        <Input
                          id={field.name}
                          name={field.name}
                          type={field.type || "text"}
                          value={formData[field.name] || ""}
                          onChange={handleChange}
                          required={field.required}
                          placeholder={field.placeholder || ""}
                          data-testid={`contact-input-${field.name}`}
                        />
                      </div>
                    ))}
                  </div>
                  {textareaFields.map((field) => (
                    <div key={field.name}>
                      <Label htmlFor={field.name}>{field.label}{field.required ? " *" : ""}</Label>
                      <Textarea
                        id={field.name}
                        name={field.name}
                        value={formData[field.name] || ""}
                        onChange={handleChange}
                        required={field.required}
                        placeholder={field.placeholder || ""}
                        rows={5}
                        data-testid={`contact-input-${field.name}`}
                      />
                    </div>
                  ))}
                  <ContactTimePicker value={contactTime} onChange={setContactTime} />
                  <Button type="submit" disabled={loading} className="w-full sm:w-auto bg-teal hover:bg-teal-dark text-white font-semibold" size="lg" data-testid="contact-submit-btn">
                    {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Sending...</> : <>Send Message<Send className="w-5 h-5 ml-2" /></>}
                  </Button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
